
import React, { useState, useEffect } from 'react';
import { X, Clock, ChevronRight, ChevronLeft, CheckCircle2, User, Package, AlertCircle, Trash2, Plus, Minus, Percent } from 'lucide-react';
import { Product, ProductSize } from '../../types';
import WizardProductSelector, { WizardItem } from './WizardProductSelector';

interface PreOrderWizardProps {
  isOpen: boolean;
  onClose: () => void;
  products: Product[];
  onComplete: (payload: {
    items: { product: Product; size: ProductSize; quantity: number }[];
    details: { customerName: string; discount: number };
  }) => void;
}

const PreOrderWizard: React.FC<PreOrderWizardProps> = ({ isOpen, onClose, products, onComplete }) => {
  const [step, setStep] = useState(0);
  const [selectedItems, setSelectedItems] = useState<WizardItem[]>([]);
  const [customerName, setCustomerName] = useState('');
  const [discount, setDiscount] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setStep(0);
      setSelectedItems([]);
      setCustomerName('');
      setDiscount(0);
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleFinish = () => {
    if (!customerName.trim()) {
      setError("Customer Name is mandatory for Pre-Order records.");
      return;
    }
    if (selectedItems.length === 0) {
      setError("Add at least one item.");
      setStep(0);
      return;
    }

    onComplete({
      items: selectedItems,
      details: { customerName, discount }
    });
    onClose();
  };

  const handleContinue = () => {
    if (step === 0) {
      if (selectedItems.length > 0) {
        setStep(1);
      } else {
        setError("Please add an item first.");
      }
    } else if (step === 1) {
      if (!customerName.trim()) {
        setError("Customer Name is mandatory.");
        return;
      }
      setStep(step + 1);
    } else {
      setStep(step + 1);
    }
  };

  const steps = [
    { label: 'Select', icon: Package },
    { label: 'Client', icon: User },
    { label: 'Done', icon: CheckCircle2 }
  ];

  return (
    <div className="fixed inset-0 bg-black/75 backdrop-blur-md z-[200] flex items-center justify-center p-4 animate-in fade-in duration-300">
      <div className="bg-white dark:bg-gray-950 rounded-[1.5rem] shadow-2xl w-full max-w-md overflow-hidden flex flex-col border border-white/10 relative max-h-[85vh]">
        
        <div className="p-4 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50 flex justify-between items-center shrink-0">
            <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center text-white shadow-md">
                    <Clock size={18} />
                </div>
                <div>
                    <h3 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-tight">Pre-Order Release</h3>
                    <p className="text-[8px] font-bold text-gray-400 uppercase tracking-widest">Order Fulfillment</p>
                </div>
            </div>
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-all">
                <X size={18} />
            </button>
        </div>

        {error && (
            <div className="px-4 pt-4 shrink-0">
                <div className="p-2.5 bg-red-50 dark:bg-red-900/30 border border-red-100 dark:border-red-900/50 rounded-lg flex items-center gap-2 text-red-600 dark:text-red-400 text-[10px] animate-in slide-in-from-top-2">
                    <AlertCircle size={14} className="shrink-0" />
                    <p className="font-black uppercase tracking-tight leading-tight">{error}</p>
                </div>
            </div>
        )}

        <div className="px-10 pt-5 flex justify-between items-center shrink-0">
            {steps.map((s, idx) => (
                <div key={idx} className="flex flex-col items-center gap-1 relative flex-1">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-black z-10 transition-all duration-500 ${
                        step >= idx ? 'bg-amber-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-400'
                    }`}>
                        <s.icon size={10} />
                    </div>
                    {idx < steps.length - 1 && (
                        <div className={`absolute left-1/2 top-3 w-full h-[1px] -z-0 ${step > idx ? 'bg-amber-500' : 'bg-gray-100 dark:bg-gray-800'}`}></div>
                    )}
                </div>
            ))}
        </div>

        <div className="p-6 flex-1 overflow-y-auto scrollbar-hide">
            {step === 0 && (
                <div className="animate-in fade-in zoom-in-95 duration-300 py-2">
                    <WizardProductSelector
                      products={products}
                      selectedItems={selectedItems}
                      onUpdateItems={setSelectedItems}
                      themeColor="amber"
                    />
                </div>
            )}

            {step === 1 && (
                <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300 py-1">
                    <div className="space-y-1">
                        <label className="text-[8px] font-black text-gray-400 uppercase tracking-widest ml-1">Customer Name *</label>
                        <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" size={14} />
                            <input
                                autoFocus
                                value={customerName}
                                onChange={e => { setCustomerName(e.target.value); setError(null); }}
                                className="w-full pl-9 pr-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-lg font-black text-[11px] uppercase outline-none focus:ring-4 focus:ring-amber-500/10 transition-all dark:text-white"
                                placeholder="Enter Full Name"
                            />
                        </div>
                        <p className="text-[8px] text-gray-400 font-bold uppercase tracking-tight mt-1 ml-1">Must match pre-order reservation identity</p>
                    </div>
                    <div className="space-y-1">
                        <label className="text-[8px] font-black text-gray-400 uppercase tracking-widest ml-1">Discount %</label>
                        <div className="relative">
                            <Percent className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" size={14} />
                            <input
                                type="number"
                                min={0}
                                max={100}
                                value={discount}
                                onChange={e => setDiscount(Math.min(100, Math.max(0, Number(e.target.value))))}
                                className="w-full pl-9 pr-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-lg font-black text-[11px] outline-none focus:ring-4 focus:ring-amber-500/10 transition-all dark:text-white"
                                placeholder="0"
                            />
                        </div>
                        <p className="text-[8px] text-gray-400 font-bold uppercase tracking-tight mt-1 ml-1">Leave 0 for full price</p>
                    </div>
                </div>
            )}

            {step === 2 && (
                <div className="text-center space-y-4 animate-in fade-in zoom-in-95 duration-500 py-1">
                    <div className="w-12 h-12 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 rounded-full flex items-center justify-center mx-auto border border-emerald-100">
                        <CheckCircle2 size={24} />
                    </div>
                    <div>
                        <h4 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-tighter leading-none">Confirm Release</h4>
                        <p className="text-[8px] text-gray-400 font-bold uppercase tracking-widest mt-1">Reduction Authorized for {selectedItems.reduce((a,b)=>a+b.quantity,0)} Items</p>
                    </div>
                    
                    <div className="bg-white dark:bg-gray-900 p-4 rounded-xl border border-dashed border-gray-200 dark:border-gray-800 text-left space-y-2 max-h-40 overflow-y-auto">
                        <div className="flex justify-between items-center pb-2 border-b border-gray-50 dark:border-gray-800">
                            <span className="text-[8px] font-black text-gray-400 uppercase">Client</span>
                            <span className="text-[10px] font-black text-amber-600 uppercase">{customerName}</span>
                        </div>
                        {discount > 0 && (
                            <div className="flex justify-between items-center py-1 border-b border-gray-50 dark:border-gray-800">
                                <span className="text-[8px] font-black text-gray-400 uppercase">Discount</span>
                                <span className="text-[10px] font-black text-emerald-600">{discount}% OFF</span>
                            </div>
                        )}
                        {selectedItems.map((item, idx) => (
                          <div key={idx} className="flex justify-between items-center text-[9px] py-1 border-b border-gray-50 dark:border-gray-800 last:border-0">
                            <span className="font-bold dark:text-white truncate max-w-[180px]">{item.product.name} (V-{item.size.size})</span>
                            <span className="font-black text-amber-500">x{item.quantity}</span>
                          </div>
                        ))}
                    </div>
                </div>
            )}
        </div>

        <div className="p-4 border-t border-gray-100 dark:border-gray-800 flex gap-2.5 bg-gray-50/30 dark:bg-gray-900/30 shrink-0">
            {step > 0 && (
                <button 
                    onClick={() => setStep(step - 1)}
                    className="flex-1 py-2.5 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-300 rounded-lg font-black text-[9px] uppercase tracking-widest border border-gray-200 dark:border-gray-700 hover:bg-gray-100 transition-all flex items-center justify-center gap-1.5"
                >
                    <ChevronLeft size={14} /> Back
                </button>
            )}
            <button 
                onClick={step === 2 ? handleFinish : handleContinue}
                className="flex-[2] py-2.5 bg-amber-500 text-white rounded-lg font-black text-[9px] uppercase tracking-[0.15em] shadow-lg shadow-amber-100 dark:shadow-none hover:bg-amber-600 transition-all flex items-center justify-center gap-1.5 group"
            >
                {step === 2 ? 'Release & Log' : 'Continue'} 
                {step < 2 && <ChevronRight size={14} className="group-hover:translate-x-0.5 transition-transform" />}
            </button>
        </div>
      </div>
    </div>
  );
};

export default PreOrderWizard;
